package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun OrderScreen(
    initialPageIndex: Int = 0,
    onPageChange: (Int) -> Unit = {},
    onBack: () -> Unit,
    onOrderClick: (Order) -> Unit,
    onCreateOrder: (Supplier) -> Unit
) {
    val scope = rememberCoroutineScope()
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var suppliers by remember { mutableStateOf<List<Supplier>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        repository.getSuppliers().fold(
            onSuccess = {
                suppliers = it
                isLoading = false
            },
            onFailure = {
                error = it.message
                isLoading = false
            }
        )
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { ScreenTitle("Orders") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (error != null) {
                    Text("Error: $error", modifier = Modifier.align(Alignment.Center), color = MaterialTheme.colorScheme.error)
                } else if (!isLoading && suppliers.isEmpty()) {
                    Text("No suppliers found.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    val pagerState = rememberPagerState(
                        initialPage = initialPageIndex.coerceIn(0, maxOf(0, suppliers.size - 1)),
                        pageCount = { suppliers.size }
                    )

                    LaunchedEffect(pagerState.currentPage) {
                        onPageChange(pagerState.currentPage)
                    }

                    Column {
                        ScrollableTabRow(
                            selectedTabIndex = pagerState.currentPage,
                            edgePadding = 16.dp,
                            containerColor = MaterialTheme.colorScheme.surface,
                            contentColor = MaterialTheme.colorScheme.primary,
                            divider = {},
                            indicator = { tabPositions ->
                                if (pagerState.currentPage < tabPositions.size) {
                                    TabRowDefaults.SecondaryIndicator(
                                        modifier = Modifier.tabIndicatorOffset(tabPositions[pagerState.currentPage]),
                                        color = MaterialTheme.colorScheme.secondary
                                    )
                                }
                            }
                        ) {
                            suppliers.forEachIndexed { index, supplier ->
                                Tab(
                                    selected = pagerState.currentPage == index,
                                    onClick = { scope.launch { pagerState.animateScrollToPage(index) } },
                                    text = { Text(supplier.name) }
                                )
                            }
                        }

                        HorizontalPager(
                            state = pagerState,
                            modifier = Modifier.fillMaxSize()
                        ) { page ->
                            SupplierOrdersView(
                                supplier = suppliers[page],
                                repository = repository,
                                onOrderClick = onOrderClick,
                                onCreateOrder = { onCreateOrder(suppliers[page]) }
                            )
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }
}

@Composable
fun SupplierOrdersView(
    supplier: Supplier,
    repository: SSInventoryRepository,
    onOrderClick: (Order) -> Unit,
    onCreateOrder: () -> Unit,
    showCreateButton: Boolean = true
) {
    val userRole = LocalUserRole.current
    var orders by remember(supplier.id) { mutableStateOf<List<Order>>(emptyList()) }
    var orderValuations by remember(supplier.id) { mutableStateOf<Map<String, Double>>(emptyMap()) }
    var isLoading by remember(supplier.id) { mutableStateOf(true) }

    LaunchedEffect(supplier.id) {
        isLoading = true
        val itemsResult = repository.getItems(supplier.id)
        val itemsMap = if (itemsResult.isSuccess) itemsResult.getOrThrow().associateBy { it.id } else emptyMap()

        repository.getOrders(supplier.id).onSuccess { orderList ->
            orders = orderList
            if (userRole == UserRole.ADMIN) {
                val vals = mutableMapOf<String, Double>()
                orderList.forEach { order ->
                    repository.getOrderItems(order.id).onSuccess { orderItems ->
                        var total = 0.0
                        orderItems.forEach { orderItem ->
                            val item = itemsMap[orderItem.itemId]
                            if (item != null) {
                                total += orderItem.qtyOrdered * item.price
                            }
                        }
                        vals[order.id] = total
                    }
                }
                orderValuations = vals
            }
            isLoading = false
        }
    }

    Column(modifier = Modifier.fillMaxSize().padding(horizontal = 12.dp, vertical = 8.dp)) {
        if (showCreateButton) {
            Button(
                onClick = onCreateOrder,
                modifier = Modifier.fillMaxWidth().height(56.dp),
                shape = MaterialTheme.shapes.medium,
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary)
            ) {
                Text("NEW ORDER FOR ${supplier.name.uppercase()}")
            }
            Spacer(modifier = Modifier.height(12.dp))
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        } else if (orders.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text("No orders for this supplier")
            }
        } else {
            LazyColumn(modifier = Modifier.fillMaxSize()) {
                items(orders) { order ->
                    OrderRow(
                        order = order.copy(supplierName = supplier.name), 
                        valuation = orderValuations[order.id],
                        onClick = { onOrderClick(order) }
                    )
                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                }
            }
        }
    }
}

@Composable
fun OrderRow(order: Order, valuation: Double? = null, onClick: () -> Unit) {
    val userRole = LocalUserRole.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(16.dp)
    ) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(
                text = order.supplierName ?: "Supplier #${order.supplierId}",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
            StatusBadge(status = order.status)
        }
        Spacer(modifier = Modifier.height(4.dp))
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text("Created: ${order.createdAt.substringBefore(" ")}", style = MaterialTheme.typography.bodySmall)
            Text("Days: ${order.daysCalculated}", style = MaterialTheme.typography.bodySmall)
        }
        if (order.receivedAt != null) {
            Spacer(modifier = Modifier.height(2.dp))
            Text("Received: ${order.receivedAt.substringBefore(" ")}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.secondary)
        }
        if (userRole == UserRole.ADMIN && valuation != null) {
            Spacer(modifier = Modifier.height(2.dp))
            Text("Total Value: £${formatDecimal(valuation)}", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
        }
    }
}

@Composable
fun StatusBadge(status: String) {
    val containerColor = when (status) {
        "Received" -> Color(0xFF2E7D32)
        "Pending" -> MaterialTheme.colorScheme.secondaryContainer
        else -> MaterialTheme.colorScheme.surfaceVariant
    }
    val contentColor = when (status) {
        "Received" -> Color.White
        "Pending" -> MaterialTheme.colorScheme.onSecondaryContainer
        else -> MaterialTheme.colorScheme.onSurfaceVariant
    }

    Surface(
        color = containerColor,
        contentColor = contentColor,
        shape = MaterialTheme.shapes.small
    ) {
        Text(
            text = status,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
            style = MaterialTheme.typography.labelSmall
        )
    }
}
